import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Menu, X, Instagram, Facebook, ArrowUpRight, Phone, Mail, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { NAV_LINKS, EXTERNAL_LINKS, STUDIO } from "@/data/site";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => scrollTo("hero")}
          className={`font-serif-display text-xl md:text-2xl tracking-wide transition-colors ${
            scrolled ? "text-[#1A1A1A]" : "text-white"
          }`}
        >
          lsb<span className="text-[#C5A059]">·</span>studio
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => handleNav(l.id)}
              className={`link-underline text-xs uppercase tracking-[0.22em] font-medium transition-colors ${
                scrolled ? "text-[#1A1A1A]" : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
            </button>
          ))}
          {EXTERNAL_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-external-${l.label.toLowerCase()}`}
              className={`link-underline text-xs uppercase tracking-[0.22em] font-medium transition-colors ${
                scrolled ? "text-[#C5A059]" : "text-[#E6D3A3] hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:block">
          <Link
            data-testid="nav-book-btn"
            to="/book"
            className={`group inline-flex items-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.22em] font-medium transition-all duration-500 cursor-pointer ${
              scrolled
                ? "bg-[#1A1A1A] text-white hover:bg-[#C5A059]"
                : "border border-white/70 text-white hover:bg-white hover:text-[#1A1A1A]"
            }`}
          >
            Book Session
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className={`md:hidden ${scrolled ? "text-[#1A1A1A]" : "text-white"}`}
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-[#FDFBF7] border-b border-black/5"
          data-testid="nav-mobile-panel"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                onClick={() => handleNav(l.id)}
                className="text-left text-sm uppercase tracking-[0.22em] text-[#1A1A1A]"
              >
                {l.label}
              </button>
            ))}
            {EXTERNAL_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-external-${l.label.toLowerCase()}`}
                className="text-left text-sm uppercase tracking-[0.22em] text-[#C5A059]"
              >
                {l.label}
              </Link>
            ))}
            <a
              data-testid="mobile-nav-book-btn"
              href="#contact"
              onClick={(e) => { e.preventDefault(); handleNav("contact"); }}
              className="mt-2 self-start px-6 py-3 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.22em] cursor-pointer"
            >
              Book Session
            </a>
          </div>
        </motion.div>
      )}
    </header>
  );
}

export { scrollTo };
